/**
 * Pedestrian routes + citizen walkers (P1-T11). Routes are sidewalk loops:
 * rectangles inset from block edges, the plaza ring, and the bay promenade
 * strip. Positions are pure functions of (route, arc length), so citizens are
 * a handful of floats each and pick-who-is-near is a bbox test.
 */
import layout from '../world/road-layout.json';
import { prng, hash32 } from '@kota-nova/sim';
import { crowdLook, type CrowdLook } from './crowd';

const AVES = layout.aves_x as number[]; // N-S, centre x, width ave_w
const STREETS = layout.streets_z as number[]; // E-W, centre z, width street_w
const AVE_W = layout.ave_w as number;
const STREET_W = layout.street_w as number;
const SW = layout.sidewalk as { ave: number; street: number };
const BAY_EDGE = layout.bay_edge_z as number;

export interface Route {
  kind: 'rect' | 'ellipse';
  // rect: perimeter loop around [x0,x1]x[z0,z1]
  x0: number;
  x1: number;
  z0: number;
  z1: number;
  cx: number;
  cz: number;
  rx: number;
  rz: number;
  P: number; // loop length m
  weight: number;
  bb: [number, number, number, number];
}

/** Block bounds matching slice_layout.block_bounds (i = avenue gap index). */
export function blockRect(i: number, j: number): [number, number, number, number] {
  // avenue i-1 .. avenue i (slice_layout convention)
  const xL = i === 0 ? AVES[0]! - AVE_W / 2 - 40 : AVES[i - 1]! + AVE_W / 2;
  const xR = i >= AVES.length ? AVES[AVES.length - 1]! + AVE_W / 2 + 40 : AVES[i]! - AVE_W / 2;
  const zB = j === 0 ? STREETS[0]! - STREET_W / 2 - 40 : STREETS[j - 1]! + STREET_W / 2;
  const zT =
    j >= STREETS.length ? STREETS[STREETS.length - 1]! + STREET_W / 2 + 40 : STREETS[j]! - STREET_W / 2;
  return [xL, xR, zB, zT];
}

function rect(x0: number, x1: number, z0: number, z1: number, weight: number): Route {
  const P = 2 * (x1 - x0 + z1 - z0);
  return {
    kind: 'rect',
    x0,
    x1,
    z0,
    z1,
    cx: 0,
    cz: 0,
    rx: 0,
    rz: 0,
    P,
    weight,
    bb: [x0 - 4, x1 + 4, z0 - 4, z1 + 4],
  };
}

function ellipse(cx: number, cz: number, rx: number, rz: number, weight: number): Route {
  const P = Math.PI * (3 * (rx + rz) - Math.sqrt((3 * rx + rz) * (rx + 3 * rz)));
  return {
    kind: 'ellipse',
    x0: 0,
    x1: 0,
    z0: 0,
    z1: 0,
    cx,
    cz,
    rx,
    rz,
    P,
    weight,
    bb: [cx - rx - 4, cx + rx + 4, cz - rz - 4, cz + rz + 4],
  };
}

/** Build the slice route network (deterministic; matches slice_layout.json). */
export function buildRoutes(): Route[] {
  const routes: Route[] = [];
  const cells = [-2, -1, 0, 1];
  // sidewalk loops around each block column x row within the slice extent
  for (let i = 0; i <= AVES.length; i++) {
    for (let j = 0; j <= STREETS.length; j++) {
      const [xL, xR, zB, zT] = blockRect(i, j);
      if (xR < -270 || xL > 270 || zT < -270 || zB > 270) continue;
      if (zB > BAY_EDGE + 6) continue; // in the bay
      const inset = 1.6;
      const x0 = Math.max(xL, -256) + inset;
      const x1 = Math.min(xR, 256) - inset;
      const z0 = Math.max(zB, BAY_EDGE + 4) + inset;
      const z1 = Math.min(zT, 256) - inset;
      if (x1 - x0 < 12 || z1 - z0 < 12) continue;
      const midtown = zB > -118;
      routes.push(rect(x0, x1, z0, z1, midtown ? 2 : 1.5));
      void cells;
    }
  }
  // plaza ring (fountain circle)
  const pl = layout.plaza as { x0: number; x1: number; z0: number; z1: number };
  const pcx = (pl.x0 + pl.x1) / 2;
  const pcz = (pl.z0 + pl.z1) / 2;
  routes.push(ellipse(pcx, pcz, 22, 22, 3));
  routes.push(ellipse(pcx, pcz, 12, 12, 2));
  // promenade strip along the bay
  const pz0 = BAY_EDGE + 6;
  routes.push(rect(-252, 252, pz0, pz0 + 8, 3));
  routes.push(rect(-252, 252, pz0 + 14, pz0 + 20, 2));
  // sidewalk strips along each avenue and street centreline (two lanes each)
  for (const ax of AVES) {
    const off = AVE_W / 2 + SW.ave * 0.6;
    routes.push(rect(ax - off, ax + off, -256, Math.min(256, BAY_EDGE - 2), 1.2));
  }
  for (const sz of STREETS) {
    const off = STREET_W / 2 + SW.street * 0.6;
    routes.push(rect(-256, 256, sz - off, sz + off, 1.1));
  }
  return routes;
}

export interface Citizen {
  route: number;
  s0: number;
  dir: 1 | -1;
  sp: number; // m/s
  lat: number; // lateral offset from loop
  look: CrowdLook;
  seed: number;
  arch: string;
}

const ARCH = ['office', 'office', 'pupil', 'shop', 'retiree', 'tourist', 'student', 'home'] as const;

export function buildCitizens(n: number, routes: Route[]): Citizen[] {
  const r = prng(8080);
  const wsum = routes.reduce((s, q) => s + q.weight, 0);
  const out: Citizen[] = [];
  for (let i = 0; i < n; i++) {
    let a = r() * wsum;
    let ri = 0;
    for (let k = 0; k < routes.length; k++) {
      a -= routes[k]?.weight ?? 1;
      if (a <= 0) {
        ri = k;
        break;
      }
    }
    out.push({
      route: ri,
      s0: r() * (routes[ri]?.P ?? 100),
      dir: r() < 0.5 ? 1 : -1,
      sp: 1.1 + r() * 0.45,
      lat: (r() - 0.5) * 2.2,
      look: crowdLook(i + 1),
      seed: i,
      arch: ARCH[Math.floor(r() * ARCH.length)] as string,
    });
  }
  return out;
}

export interface Pose {
  x: number;
  z: number;
  yaw: number;
}

/** Position along a citizen's loop at world time t (seconds). */
export function citizenPose(c: Citizen, routes: Route[], t: number, out: Pose): void {
  const R = routes[c.route] as Route;
  let s = (((c.s0 + c.dir * c.sp * t) % R.P) + R.P) % R.P;
  if (R.kind === 'ellipse') {
    const a = (s / R.P) * Math.PI * 2;
    const rx = R.rx + c.lat * 0.8;
    const rz = R.rz + c.lat * 0.8;
    out.x = R.cx + Math.cos(a) * rx;
    out.z = R.cz + Math.sin(a) * rz;
    out.yaw = Math.atan2(-Math.sin(a) * rx * c.dir, Math.cos(a) * rz * c.dir);
    return;
  }
  const w = R.x1 - R.x0;
  const d = R.z1 - R.z0;
  const l = c.lat * 0.55;
  if (s < w) {
    out.x = R.x0 + s;
    out.z = R.z0 - l;
    out.yaw = Math.PI / 2;
  } else if ((s -= w) < d) {
    out.x = R.x1 + l;
    out.z = R.z0 + s;
    out.yaw = 0;
  } else if ((s -= d) < w) {
    out.x = R.x1 - s;
    out.z = R.z1 + l;
    out.yaw = -Math.PI / 2;
  } else {
    s -= w;
    out.x = R.x0 - l;
    out.z = R.z1 - s;
    out.yaw = Math.PI;
  }
  if (c.dir < 0) out.yaw += Math.PI;
}

/**
 * Out-and-about gate (prototype hourOut, simplified): deterministic per
 * citizen-hour presence with rush-hour curve; rain thins the crowd.
 */
export function isOut(c: Citizen, hourOfDay: number, day: number, rain: number): boolean {
  const night = hourOfDay < 5 || hourOfDay >= 23;
  const morning = hourOfDay >= 7 && hourOfDay < 10;
  const dayT = hourOfDay >= 10 && hourOfDay < 17;
  let p = night ? 0.05 : morning ? 0.45 : dayT ? 0.32 : 0.5;
  if (c.arch === 'retiree') p = hourOfDay >= 6 && hourOfDay < 11 ? 0.6 : dayT ? 0.25 : 0.05;
  if (c.arch === 'tourist') p = night ? 0.12 : 0.55;
  p *= 1 - rain * 0.35;
  const h = hash32(c.seed * 31 + Math.floor(hourOfDay) * 7 + day * 131);
  return (h % 100000) / 100000 < p;
}

/** Spatial grid keyed by 120 m cells of route bboxes (near-query fast path). */
export class CitizenGrid {
  private readonly G = 120;
  private readonly map = new Map<number, number[]>();

  constructor(citizens: Citizen[], routes: Route[]) {
    for (let ci = 0; ci < citizens.length; ci++) {
      const bb = (routes[citizens[ci]?.route ?? 0] as Route).bb;
      for (let i = Math.floor(bb[0] / this.G); i <= Math.floor(bb[1] / this.G); i++)
        for (let j = Math.floor(bb[2] / this.G); j <= Math.floor(bb[3] / this.G); j++) {
          const k = i * 10007 + j;
          const arr = this.map.get(k);
          if (arr) arr.push(ci);
          else this.map.set(k, [ci]);
        }
    }
  }

  near(x: number, z: number, r: number): number[] {
    const seen = new Set<number>();
    for (let i = Math.floor((x - r) / this.G); i <= Math.floor((x + r) / this.G); i++)
      for (let j = Math.floor((z - r) / this.G); j <= Math.floor((z + r) / this.G); j++) {
        const arr = this.map.get(i * 10007 + j);
        if (arr) for (const ci of arr) seen.add(ci);
      }
    return [...seen];
  }
}
